import { Checkbox } from '@/components/ui/checkbox';
import { QuantityStepper } from '@/pages/collabs/brief-templates/editor/controls/QuantityStepper';
import { DEFAULT_DURATION_MONTHS } from '@/pages/collabs/brief-templates/_shared/brief-content';
import { Box, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { BriefTemplateContent, BriefUsageRightsChannel } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface CollabTermsUsageRightsProps {
  content: BriefTemplateContent;
  updateContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
}

const CHANNELS: BriefUsageRightsChannel[] = ['organic', 'paid_ads'];

/**
 * Usage rights on the wizard terms step. A brief written from scratch is born
 * with no duration and no channel, and this step used to expose neither: the
 * send gate refuses such a brief, so every collab composed on the fly was a
 * dead end. The step seeds the suggested values before rendering, which is why
 * the controls here are always live — there is no "define" affordance to cross,
 * only a value to change.
 *
 * Reuses the editor bricks (month stepper, channel checkboxes) and their
 * wording: one text, one contract, wherever it is read.
 */
export const CollabTermsUsageRights = ({ content, updateContent }: CollabTermsUsageRightsProps) => {
  const { t } = useTranslation();
  const { usageRights } = content;

  const setDuration = (durationMonths: number) =>
    updateContent((prev) => ({
      ...prev,
      usageRights: { ...prev.usageRights, durationMonths },
    }));

  const toggleChannel = (channel: BriefUsageRightsChannel, checked: boolean) =>
    updateContent((prev) => {
      const picked = new Set(prev.usageRights.channels);
      if (checked) picked.add(channel);
      else picked.delete(channel);
      return {
        ...prev,
        usageRights: { ...prev.usageRights, channels: CHANNELS.filter((c) => picked.has(c)) },
      };
    });

  // Plain heading rather than a `Field.Root` like the sibling term groups: a
  // field binds its label and its id to the control inside it, and this group
  // holds two real checkboxes — both would have answered to the group's name
  // and shared one id.
  return (
    <Box w="full">
      <Text fontFamily="heading" fontSize="12.5px" fontWeight="semibold" color="dark">
        {t('invitations.modal.collab.wizard.terms.usageRightsTitle')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="4" w="full" mt="1">
        <Box>
          <Text fontSize="11px" fontWeight="semibold" color="fg.muted" mb="1.5">
            {t('collabs.briefTemplates.sections.usageRights.durationLabel')}
          </Text>
          <QuantityStepper
            value={usageRights.durationMonths ?? DEFAULT_DURATION_MONTHS}
            onChange={setDuration}
            format={(v) => t('collabs.briefTemplates.sections.usageRights.months', { count: v })}
            valueMinW="64px"
            aria-label={t('collabs.briefTemplates.sections.usageRights.durationLabel')}
          />
          <Text fontSize="10.5px" color="fg.muted" mt="1.5">
            {t('collabs.briefTemplates.sections.usageRights.fromPublication')}
          </Text>
        </Box>

        <Box>
          <Text fontSize="11px" fontWeight="semibold" color="fg.muted" mb="1.5">
            {t('collabs.briefTemplates.sections.usageRights.channelsLabel')}
          </Text>
          <VStack align="stretch" gap="2">
            {CHANNELS.map((channel) => (
              <Checkbox
                key={channel}
                colorPalette="primary"
                checked={usageRights.channels.includes(channel)}
                onCheckedChange={(details) => toggleChannel(channel, details.checked === true)}
                alignItems="flex-start"
              >
                <Text fontSize="12px" fontWeight="medium" color="dark">
                  {t(`collabs.briefTemplates.sections.usageRights.channels.${channel}.title`)}
                </Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
};
